/**
 * «Исследователь метрик»: разбор decimal-строк и форматирование значений по единице метрики.
 * Строка парсится в число только для графика; в таблице и плитках значение форматируется по единице.
 * `null` и пустая строка — «нет данных» («—»), а не ноль.
 */

const NBSP = ' '
const DASH = '—'

const formats = new Map<number, Intl.NumberFormat>()
function numberFormat(digits: number): Intl.NumberFormat {
  let format = formats.get(digits)
  if (!format) {
    format = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: digits })
    formats.set(digits, format)
  }
  return format
}

/** Decimal-строка → число; `null`, пустое и нечисловое → `null`. */
export function parseDecimal(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const result = typeof value === 'number' ? value : Number(String(value).trim())
  return Number.isFinite(result) ? result : null
}

/** Число без единицы: от 100 по модулю — целое, мельче — до двух знаков. */
function plain(value: number): string {
  return numberFormat(Math.abs(value) >= 100 ? 0 : 2).format(value)
}

/** Атомы составных единиц бизнес-слоя: в числителе и в знаменателе («₽ на шт.·день»). */
const NUMERATOR_ATOMS: Record<string, string> = { RUB: '₽', USD: '$', units: 'шт.', points: 'б.', days: 'дн.' }
const DENOMINATOR_ATOMS: Record<string, string> = { ...NUMERATOR_ATOMS, days: 'день' }
const SUPERSCRIPTS: Record<string, string> = { '2': '²', '3': '³' }
/** Устаревшие написания составных единиц: `unit_days` — это штуко-дни (`units*days`). */
const UNIT_ALIASES: Record<string, string> = { unit_days: 'units*days', units_days: 'units*days' }

function unitFactors(side: string, names: Record<string, string>): string[] | null {
  const parts: string[] = []
  for (const factor of side.split('*')) {
    const match = /^(RUB|USD|units|points|days)(?:\^(\d))?$/.exec(factor)
    if (!match) return null
    const power = match[2] ? (SUPERSCRIPTS[match[2]] ?? `^${match[2]}`) : ''
    parts.push(`${names[match[1]]}${power}`)
  }
  return parts
}

/**
 * Составная единица бизнес-слоя по-русски: `RUB/units` → «₽ на шт.», `RUB/units*days` → «₽ на шт.·день»,
 * `units/days` → «шт. на день», `units*days` → «шт.·дн.». Простые и неизвестные единицы возвращаются как есть.
 */
export function unitLabel(unit: string): string {
  if (!unit) return ''
  const canonical = unit.replace(/\bunits?_days\b/g, UNIT_ALIASES.unit_days)
  if (!/[/*^]/.test(canonical)) return NUMERATOR_ATOMS[canonical] ?? unit
  const [top, bottom, ...rest] = canonical.split('/')
  if (rest.length) return unit
  const numerator = top === '1' && bottom !== undefined ? ['1'] : unitFactors(top, NUMERATOR_ATOMS)
  const denominator = bottom === undefined ? null : unitFactors(bottom, DENOMINATOR_ATOMS)
  if (!numerator || (bottom !== undefined && !denominator)) return unit
  return denominator ? `${numerator.join('·')} на ${denominator.join('·')}` : numerator.join('·')
}


/** «1 234 ₽», «1 234 шт», «12,3 %» (ratio × 100), составные единицы по-русски, иначе число и единица. */
export function formatMetricValue(value: number | null, unit: string): string {
  if (value === null) return DASH
  switch (unit) {
    case 'RUB':
      return `${plain(value)}${NBSP}₽`
    case 'USD':
      return `${plain(value)}${NBSP}$`
    case 'units':
      return `${plain(value)}${NBSP}шт`
    case 'ratio':
      return `${numberFormat(1).format(value * 100)}${NBSP}%`
    case 'percent':
      return `${numberFormat(1).format(value)}${NBSP}%`
    case 'days':
      return `${plain(value)}${NBSP}дн.`
    case 'points':
      return `${plain(value)}${NBSP}б.`
    default: {
      const label = unitLabel(unit)
      return label ? `${plain(value)}${NBSP}${label}` : plain(value)
    }
  }
}

/** То же для decimal-строки из ответа. */
export const formatMetricString = (value: unknown, unit: string): string => formatMetricValue(parseDecimal(value), unit)

/** Короткая запись оси графика: «1,2 млн», «15 тыс.»; ratio — проценты. */
export function formatAxisValue(value: number, unit: string): string {
  if (unit === 'ratio') return `${numberFormat(0).format(value * 100)}${NBSP}%`
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${numberFormat(1).format(value / 1_000_000)}${NBSP}млн`
  if (abs >= 10_000) return `${numberFormat(0).format(value / 1_000)}${NBSP}тыс.`
  return numberFormat(abs >= 100 ? 0 : 2).format(value)
}

export const UNIT_NAMES: Record<string, string> = {
  RUB: 'рубли',
  USD: 'доллары',
  units: 'штуки',
  ratio: 'доля',
  percent: 'проценты',
  days: 'дни',
  points: 'баллы',
}

export const unitName = (unit: string): string => UNIT_NAMES[unit] ?? (unit ? unitLabel(unit) : 'без единицы')

const WEEK = /^(\d{4})-W(\d{2})$/
const MONTH = /^(\d{4})-(\d{2})$/
const DAY = /^(\d{4})-(\d{2})-(\d{2})/

/** Ключ периода → подпись оси: день `01.09`, неделя `нед. 41`, месяц `09.2026`. */
export function formatTimeKey(key: string, dimension: string): string {
  if (dimension === 'day') {
    const match = DAY.exec(key)
    return match ? `${match[3]}.${match[2]}` : key
  }
  if (dimension === 'week') {
    const match = WEEK.exec(key)
    return match ? `нед. ${Number(match[2])}` : key
  }
  if (dimension === 'month') {
    const match = MONTH.exec(key)
    return match ? `${match[2]}.${match[1]}` : key
  }
  return key
}

/** Полная подпись периода для подсказки и таблицы: `2026-W41` → «неделя 41, 2026». */
export function formatTimeKeyFull(key: string, dimension: string): string {
  if (dimension === 'week') {
    const match = WEEK.exec(key)
    return match ? `неделя ${Number(match[2])}, ${match[1]}` : key
  }
  if (dimension === 'day') {
    const match = DAY.exec(key)
    return match ? `${match[3]}.${match[2]}.${match[1]}` : key
  }
  if (dimension === 'month') {
    const match = MONTH.exec(key)
    return match ? `${match[2]}.${match[1]}` : key
  }
  return key
}
